export type AppErrorCode =
  | "BOOTSTRAP_FAILURE"
  | "EXTERNAL_DEPENDENCY_FAILURE"
  | "ASSET_LOAD_FAILURE";

interface AppErrorOptions {
  code: AppErrorCode;
  correlationId?: string;
  retryable?: boolean;
  cause?: unknown;
  metadata?: Record<string, unknown>;
}

export class AppError extends Error {
  code: AppErrorCode;
  correlationId?: string;
  retryable: boolean;
  cause?: unknown;
  metadata?: Record<string, unknown>;

  constructor(message: string, options: AppErrorOptions) {
    super(message);
    this.name = new.target.name;
    this.code = options.code;
    this.correlationId = options.correlationId;
    this.retryable = options.retryable ?? false;
    this.cause = options.cause;
    this.metadata = options.metadata;
  }
}

interface ExternalDependencyErrorOptions {
  dependency: string;
  stage: string;
  correlationId?: string;
  retryable?: boolean;
  cause?: unknown;
  metadata?: Record<string, unknown>;
}

export class ExternalDependencyError extends AppError {
  dependency: string;
  stage: string;

  constructor(message: string, options: ExternalDependencyErrorOptions) {
    super(message, {
      code: "EXTERNAL_DEPENDENCY_FAILURE",
      correlationId: options.correlationId,
      retryable: options.retryable,
      cause: options.cause,
      metadata: options.metadata,
    });
    this.dependency = options.dependency;
    this.stage = options.stage;
  }
}

interface AssetLoadErrorOptions {
  assetUrl: string;
  stage: string;
  correlationId?: string;
  retryable?: boolean;
  cause?: unknown;
  metadata?: Record<string, unknown>;
}

export class AssetLoadError extends ExternalDependencyError {
  assetUrl: string;

  constructor(message: string, options: AssetLoadErrorOptions) {
    super(message, {
      dependency: "remote-3d-asset",
      stage: options.stage,
      correlationId: options.correlationId,
      retryable: options.retryable,
      cause: options.cause,
      metadata: options.metadata,
    });
    this.code = "ASSET_LOAD_FAILURE";
    this.assetUrl = options.assetUrl;
  }
}

interface BootstrapErrorOptions {
  stage: string;
  correlationId?: string;
  retryable?: boolean;
  cause?: unknown;
  metadata?: Record<string, unknown>;
}

export class BootstrapError extends AppError {
  stage: string;

  constructor(message: string, options: BootstrapErrorOptions) {
    super(message, {
      code: "BOOTSTRAP_FAILURE",
      correlationId: options.correlationId,
      retryable: options.retryable,
      cause: options.cause,
      metadata: options.metadata,
    });
    this.stage = options.stage;
  }
}

export const normalizeError = (error: unknown) => {
  if (error instanceof AppError) {
    return {
      name: error.name,
      message: error.message,
      code: error.code,
      correlationId: error.correlationId,
      retryable: error.retryable,
      metadata: error.metadata,
      cause:
        error.cause instanceof Error
          ? { name: error.cause.name, message: error.cause.message }
          : error.cause,
    };
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
    };
  }

  return {
    message: String(error),
  };
};
